import { devWarn } from '../../utils/devLog';
import { SoundConfig, SoundKey } from './types';

/**
 * Sound configurations for each generated SFX.
 */
export const SOUND_CONFIGS: Record<SoundKey, SoundConfig> = {
  shoot: {
    type: 'square',
    frequency: 800,
    duration: 0.08,
    volume: 0.15,
    ramp: 'down',
    detune: 50,
  },
  enemy_death: {
    type: 'sine',
    frequency: 400,
    duration: 0.12,
    volume: 0.25,
    ramp: 'down',
  },
  soldier_multiply: {
    type: 'sine',
    frequency: 600,
    duration: 0.1,
    volume: 0.2,
    ramp: 'both',
  },
  weapon_unlock: {
    type: 'triangle',
    frequency: 523,
    duration: 0.3,
    volume: 0.25,
    ramp: 'up',
  },
  game_over: {
    type: 'sawtooth',
    frequency: 300,
    duration: 0.5,
    volume: 0.2,
    ramp: 'down',
  },
  level_complete: {
    type: 'triangle',
    frequency: 440,
    duration: 0.4,
    volume: 0.3,
    ramp: 'up',
  },
  button_tap: {
    type: 'sine',
    frequency: 1000,
    duration: 0.05,
    volume: 0.1,
    ramp: 'down',
  },
};

export function playGeneratedSound(
  audioContext: AudioContext | null,
  config: SoundConfig,
  volumeMultiplier: number
): void {
  if (!audioContext) return;

  try {
    const { type, frequency, duration, volume, ramp, detune } = config;
    const finalVolume = volume * volumeMultiplier;

    const oscillator = audioContext.createOscillator();
    oscillator.type = type;
    oscillator.frequency.value = frequency;

    if (detune) {
      oscillator.detune.value = (Math.random() - 0.5) * detune * 2;
    }

    const gainNode = audioContext.createGain();
    const currentTime = audioContext.currentTime;

    switch (ramp) {
      case 'up':
        gainNode.gain.setValueAtTime(0, currentTime);
        gainNode.gain.linearRampToValueAtTime(finalVolume, currentTime + duration * 0.3);
        gainNode.gain.linearRampToValueAtTime(finalVolume, currentTime + duration * 0.7);
        gainNode.gain.linearRampToValueAtTime(0, currentTime + duration);
        break;
      case 'down':
        gainNode.gain.setValueAtTime(finalVolume, currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + duration);
        break;
      case 'both':
        gainNode.gain.setValueAtTime(0, currentTime);
        gainNode.gain.linearRampToValueAtTime(finalVolume, currentTime + duration * 0.2);
        gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + duration);
        break;
      default:
        gainNode.gain.setValueAtTime(finalVolume, currentTime);
        gainNode.gain.linearRampToValueAtTime(0, currentTime + duration);
    }

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(currentTime);
    oscillator.stop(currentTime + duration + 0.1);

    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  } catch (error) {
    devWarn('AudioSystem: Error playing sound:', error);
  }
}
